import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const escapeHtml = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

/**
 * Direct-download PDF helper.
 *
 * Kannada is first rendered by the browser in a hidden HTML page, then each
 * page is captured and inserted into jsPDF as PNG. This avoids jsPDF drawing
 * Kannada text directly.
 *
 * NOTE: PDF text is visual/rasterized, so text selection/search is limited.
 */
export const printTableReport = async ({
  title,
  data = [],
  metaLines = [],
  orientation = "landscape",
  paperSize = "A4",
  fileName = "report.pdf",
  rowsPerPage = 22,
}) => {
  if (!Array.isArray(data) || !data.length) return false;

  const headers = Object.keys(data[0] || {}).filter((key) => key !== "__blank");
  if (!headers.length) return false;

  const normalizedFileName = String(fileName || "report.pdf").toLowerCase().endsWith(".pdf")
    ? String(fileName || "report.pdf")
    : `${fileName || "report"}.pdf`;

  const doc = new jsPDF({
    orientation,
    unit: "pt",
    format: String(paperSize || "A4").toLowerCase(),
    compress: true,
  });

  const pdfWidth = doc.internal.pageSize.getWidth();
  const pdfHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || 22);
  const pageCount = Math.ceil(data.length / safeRowsPerPage);

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const pageRows = data.slice(
      pageIndex * safeRowsPerPage,
      pageIndex * safeRowsPerPage + safeRowsPerPage,
    );

    const pageRoot = document.createElement("div");
    pageRoot.setAttribute("data-pdf-capture", "true");
    pageRoot.style.position = "fixed";
    pageRoot.style.left = "-20000px";
    pageRoot.style.top = "0";
    pageRoot.style.width = orientation === "landscape" ? "1500px" : "1000px";
    pageRoot.style.background = "#ffffff";
    pageRoot.style.color = "#000000";
    pageRoot.style.padding = "20px";
    pageRoot.style.boxSizing = "border-box";
    pageRoot.style.fontFamily = '"Noto Sans Kannada", "Nirmala UI", "Tunga", Arial, sans-serif';
    pageRoot.style.letterSpacing = "normal";
    pageRoot.style.wordSpacing = "normal";
    pageRoot.style.textRendering = "optimizeLegibility";

    const metaHtml = (metaLines || [])
      .filter(Boolean)
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join("");

    const headHtml = headers
      .map((header) => `<th>${escapeHtml(header)}</th>`)
      .join("");

    const bodyHtml = pageRows
      .map((row) => {
        if (row && row.__blank === true) {
          return `<tr class="blank-row"><td colspan="${headers.length}">&nbsp;</td></tr>`;
        }

        return `<tr>${headers
          .map((header) => `<td>${escapeHtml(row?.[header] ?? "")}</td>`)
          .join("")}</tr>`;
      })
      .join("");

    pageRoot.innerHTML = `
      <style>
        .pdf-report-page, .pdf-report-page * { box-sizing: border-box; }
        .pdf-report-page {
          width: 100%;
          background: #fff;
          color: #000;
          font-family: "Noto Sans Kannada", "Nirmala UI", "Tunga", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: normal;
          word-spacing: normal;
          text-rendering: optimizeLegibility;
        }
        .pdf-report-title {
          font-family: Arial, sans-serif;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .pdf-report-meta {
          font-family: Arial, sans-serif;
          font-size: 11px;
          margin-bottom: 10px;
        }
        .pdf-report-page table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }
        .pdf-report-page th {
          border: 1px solid #999;
          background: #eee;
          padding: 5px 3px;
          font-family: Arial, sans-serif;
          font-size: 9px;
          font-weight: 700;
          line-height: 1.25;
          text-align: center;
          vertical-align: middle;
          white-space: normal;
          overflow-wrap: break-word;
        }
        .pdf-report-page td {
          border: 1px solid #ccc;
          padding: 4px 3px;
          font-family: "Noto Sans Kannada", "Nirmala UI", "Tunga", Arial, sans-serif;
          font-size: 10px;
          line-height: 1.5;
          vertical-align: middle;
          white-space: normal;
          word-break: normal;
          overflow-wrap: break-word;
          letter-spacing: normal;
          word-spacing: normal;
          font-kerning: normal;
          font-variant-ligatures: normal;
          text-rendering: optimizeLegibility;
        }
        .pdf-report-page .blank-row td {
          height: 14px;
          border-left: 0;
          border-right: 0;
        }
      </style>
      <div class="pdf-report-page">
        <div class="pdf-report-title">${escapeHtml(title || "Report")}</div>
        <div class="pdf-report-meta">
          ${metaHtml}
          <div>Page ${pageIndex + 1} / ${pageCount}</div>
        </div>
        <table>
          <thead><tr>${headHtml}</tr></thead>
          <tbody>${bodyHtml}</tbody>
        </table>
      </div>
    `;

    document.body.appendChild(pageRoot);

    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      try {
        await Promise.all([
          document.fonts?.load?.('14px "Noto Sans Kannada"'),
          document.fonts?.load?.('14px "Nirmala UI"'),
        ].filter(Boolean));
      } catch (fontError) {
        console.warn("PDF font preload warning:", fontError);
      }

      await waitForPaint();
      await new Promise((resolve) => setTimeout(resolve, 120));

      const canvas = await html2canvas(pageRoot, {
        scale: 2.5,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: false,
        logging: false,
        width: pageRoot.scrollWidth,
        height: pageRoot.scrollHeight,
        windowWidth: pageRoot.scrollWidth,
        windowHeight: pageRoot.scrollHeight,
      });

      if (pageIndex > 0) {
        doc.addPage(String(paperSize || "A4").toLowerCase(), orientation);
      }

      const imageData = canvas.toDataURL("image/png");
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;
      const ratio = canvas.width / canvas.height;

      let drawWidth = usableWidth;
      let drawHeight = drawWidth / ratio;

      if (drawHeight > usableHeight) {
        drawHeight = usableHeight;
        drawWidth = drawHeight * ratio;
      }

      const x = (pdfWidth - drawWidth) / 2;
      const y = margin;

      doc.addImage(
        imageData,
        "PNG",
        x,
        y,
        drawWidth,
        drawHeight,
        undefined,
        "FAST",
      );
    } finally {
      pageRoot.remove();
    }
  }

  doc.save(normalizedFileName);
  return true;
};
